import type { ReactNode } from 'react';
import { Check, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useI18nContext } from '@/i18n/i18n-react';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

/**
 * Plain-language help behind an (i): opens on click/tap (nothing in the
 * composer depends on hover), closes on Esc or outside.
 */
export function FieldHint({
  label,
  children,
}: {
  /** Names the field, e.g. "Ajuda: Título". */
  label: string;
  children: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          className={cn(
            'size-6 shrink-0 rounded-full text-muted-foreground hover:text-foreground',
            mobileLayer.touch
          )}
        >
          <Info className="size-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 text-sm leading-relaxed text-muted-foreground"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * One step of the composer: its number (a check once complete), title,
 * optional help and an aside on the right of the title (e.g. the timezone).
 */
export function ComposerSection({
  step,
  title,
  complete,
  hint,
  aside,
  children,
}: {
  step: number;
  title: string;
  complete: boolean;
  hint?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const { LL } = useI18nContext();
  const headingId = `composer-section-${step}`;
  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
            complete
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground'
          )}
        >
          {complete ? <Check className="size-3.5" strokeWidth={3} /> : step}
        </span>
        <h2 id={headingId} className="text-base font-semibold">
          {title}
          {complete && (
            <span className="sr-only">
              {' '}
              ({LL.campaignComposer.sections.done()})
            </span>
          )}
        </h2>
        {hint && (
          <FieldHint
            label={LL.campaignComposer.sections.hintLabel({ section: title })}
          >
            {hint}
          </FieldHint>
        )}
        {aside && (
          <span className="ml-auto text-sm text-muted-foreground">{aside}</span>
        )}
      </div>
      <div className="space-y-6 pl-0 md:pl-[34px]">{children}</div>
    </section>
  );
}
